import { useEffect, useState } from 'react';
import { plugin, debug, LightGrid, useLightContext } from 'light-component';
import * as Base from '@base';
import { Icons, useTranslation } from '@common';
import { useBreakpoint } from '../../../hooks';
import { Require } from '../component';
import styles from './style.module.less';
import type { CustomOptionProps } from './types';

export function WidgetOptions(props: CustomOptionProps): React.ReactElement {
  debug.widget('form option', props);
  const defaultOptions = [
    { value: '01', title: 'item1' },
    { value: '02', title: 'item2' }
  ];
  const [t] = useTranslation();
  const { md } = useBreakpoint();
  const { active } = useLightContext();
  const [options, setOptions] = useState(props.value ?? defaultOptions);
  const [modalOptions, setModalOptions] = useState<CustomOptionProps['value']>(props.value ?? defaultOptions);
  const [open, setOpen] = useState<boolean>(false);

  const isFocus = props?.focus === props.name;
  const onOptionsChange = (value: CustomOptionProps['value']): void => {
    const isOptionEmpty = value.length === 0 || value[0]?.title == null;
    setOptions(isOptionEmpty ? [{ value: '01', title: '' }] : value);
  };

  const onModalOptionsChange = (value: CustomOptionProps['value']): void => {
    const isOptionEmpty = value.length === 0 || value[0]?.title == null;
    setModalOptions(isOptionEmpty ? [{ value: '01', title: '' }] : value);
  };

  const onModalClose = (ok: boolean): void => {
    if (ok) {
      setOptions(modalOptions);
    }
    setOpen(false);
  };

  const onModalOpen = (): void => {
    setModalOptions(options);
    setOpen(true);
  };

  // active 题目切换，重置option选项
  useEffect(() => {
    setOptions(props.value ?? defaultOptions);
  }, [active]);

  useEffect(() => {
    props.onChange?.(options);
  }, [options]);

  const keyGenerator = (): string => {
    if (options.length < 9) {
      return `0${options.length + 1}`;
    }
    return String(options.length + 1);
  };

  const modalKeyGenerator = (): string => {
    if (modalOptions.length < 9) {
      return `0${modalOptions.length + 1}`;
    }
    return String(modalOptions.length + 1);
  };

  return (
    <div className={isFocus ? styles.focus : (props.className ?? styles.propertyItem)}>
      <div className={styles.inputSpaceBetween}>
        <Require require={props.require} />
        <span className={styles.title}>{props.title}</span>
        <Icons
          name='ExpandAltOutlined'
          className={styles.floatRight}
          onClick={() => onModalOpen()}
        />
      </div>
      <div className={styles.option}>
        <LightGrid
          keyField='value'
          data={options}
          keyGenerator={keyGenerator}
          onChange={value => onOptionsChange(value)}
          options={[
            { title: t('标题'), type: 'input', name: 'title', width: '100%' }
          ]}
          dragAlign='left'
        />
      </div>
      <Base.Modal
        centered
        title={props.title}
        open={open}
        cancelText={t('取消')}
        okText={t('保存')}
        destroyOnClose
        onOk={() => onModalClose(true)}
        onCancel={() => onModalClose(false)}
        closable={false}
        okButtonProps={{ autoInsertSpace: false }}
        cancelButtonProps={{ autoInsertSpace: false }}
        width={840}
      >
        <LightGrid
          keyField='value'
          keyGenerator={modalKeyGenerator}
          data={modalOptions}
          onChange={value => onModalOptionsChange(value)}
          options={[
            { title: t('标题'), type: 'input', name: 'title', width: md === false ? 200 : 600 }
          ]}
          dragAlign='left'
        />
      </Base.Modal>
    </div>
  );
}

plugin.register({ kind: 'widget', type: 'option', component: WidgetOptions });
